/**
 * KWC · swatch row (product page colour picker)
 * ---------------------------------------------------------------------------
 * Wraps the colour swatches (snippets/variant-main-picker.liquid). On mobile
 * the row is a single line that scrolls sideways (kwc-product.css); this
 * element scrolls the selected swatch into view when it appears and sets
 * [data-more] while swatches are hidden off the right edge, which shows a
 * 24px fade there. Only the row scrolls, never the page. Elsewhere the row
 * wraps, nothing overflows, and both do nothing.
 */
class KwcSwatchRow extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    this.addEventListener('scroll', this.#update, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(this);
    requestAnimationFrame(() => {
      this.#revealSelected();
      this.#update();
    });
  }

  disconnectedCallback() {
    this.removeEventListener('scroll', this.#update);
    this.#resizeObserver?.disconnect();
  }

  /** Centre the selected swatch in the row if it isn't fully visible. */
  #revealSelected() {
    const selected = this.querySelector('input:checked')?.closest('label');
    if (!(selected instanceof HTMLElement) || this.scrollWidth <= this.clientWidth) return;

    const start = selected.offsetLeft;
    const end = start + selected.offsetWidth;
    if (start >= this.scrollLeft && end <= this.scrollLeft + this.clientWidth) return;

    const max = this.scrollWidth - this.clientWidth;
    const left = Math.min(max, Math.max(0, start - (this.clientWidth - selected.offsetWidth) / 2));
    this.scrollTo({ left, behavior: 'instant' });
  }

  #update = () => {
    this.toggleAttribute('data-more', this.scrollLeft + this.clientWidth < this.scrollWidth - 1);
  };
}

if (!customElements.get('kwc-swatch-row')) {
  customElements.define('kwc-swatch-row', KwcSwatchRow);
}
